import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Check, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inr = (p) => {
  const r = (p || 0) / 100;
  return "₹" + r.toLocaleString("en-IN", r % 1 ? { minimumFractionDigits: 2, maximumFractionDigits: 2 } : { maximumFractionDigits: 0 });
};
const STEPS = ["CATEGORY", "DETAILS", "VERIFY EMAIL", "ADD-ON", "REVIEW & PAY"];

const submitPayUForm = (paymentUrl, params) => {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = paymentUrl;
  form.style.display = "none";
  Object.entries(params).forEach(([k, v]) => {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = k;
    input.value = v ?? "";
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
};

const DobPicker = ({ value, onChange, testId }) => {
  const parts = (value || "").split("-");
  const [y, setY] = useState(parts[0] || "");
  const [m, setM] = useState(parts[1] || "");
  const [d, setD] = useState(parts[2] || "");
  const thisYear = new Date().getFullYear();
  const years = [];
  for (let yr = thisYear - 14; yr >= thisYear - 80; yr--) years.push(yr);
  const pick = (yy, mm, dd) => {
    setY(yy); setM(mm); setD(dd);
    if (yy && mm && dd) onChange(`${yy}-${mm}-${dd}`);
  };
  const sel = "field min-h-[48px] text-base";
  return (
    <div className="grid grid-cols-3 gap-2" data-testid={testId}>
      <select aria-label="Birth day" className={sel} value={d} onChange={(e) => pick(y, m, e.target.value)} data-testid={`${testId}-day`}>
        <option value="">DD</option>
        {Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, "0")).map((dd) => <option key={dd} value={dd}>{dd}</option>)}
      </select>
      <select aria-label="Birth month" className={sel} value={m} onChange={(e) => pick(y, e.target.value, d)} data-testid={`${testId}-month`}>
        <option value="">MM</option>
        {["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"].map((mn, i) => (
          <option key={mn} value={String(i + 1).padStart(2, "0")}>{mn}</option>
        ))}
      </select>
      <select aria-label="Birth year" className={sel} value={y} onChange={(e) => pick(e.target.value, m, d)} data-testid={`${testId}-year`}>
        <option value="">YYYY</option>
        {years.map((yr) => <option key={yr} value={yr}>{yr}</option>)}
      </select>
    </div>
  );
};

export default function RegisterPage() {
  const [params] = useSearchParams();
  const [event, setEvent] = useState(null);
  const [step, setStep] = useState(0);
  const [catId, setCatId] = useState(params.get("cat") || "");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [dob, setDob] = useState("");
  const [gender, setGender] = useState("");
  const [emgName, setEmgName] = useState("");
  const [emgPhone, setEmgPhone] = useState("");
  const [teamName, setTeamName] = useState("");
  const [pFirst, setPFirst] = useState("");
  const [pLast, setPLast] = useState("");
  const [pDob, setPDob] = useState("");
  const [pGender, setPGender] = useState("");
  const [pPhone, setPPhone] = useState("");
  const [okTerms, setOkTerms] = useState(false);
  const [okPrivacy, setOkPrivacy] = useState(false);
  const [okWaiver, setOkWaiver] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [otpToken, setOtpToken] = useState("");
  const [resendsLeft, setResendsLeft] = useState(null);
  const [addon, setAddon] = useState("standard");
  const [promo, setPromo] = useState("");
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const isDoubles = catId.endsWith("DOUBLES");
  const isSpectator = catId === "SPECTATOR";
  const category = event?.categories?.find((c) => c.id === catId);
  const addons = event?.addons || {};

  useEffect(() => {
    fetch(`${API}/event`)
      .then((r) => r.json())
      .then(setEvent)
      .catch(() => toast.error("Could not load event config"));
  }, []);

  useEffect(() => {
    if (step === 2 && !otpToken) sendOtp();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  useEffect(() => {
    if (step === 4) refreshQuote(promoApplied());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  useEffect(() => {
    if (step === 3) refreshQuote(quote?.promo || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addon]);

  const promoApplied = () => (quote && quote.promo ? promo.trim().toUpperCase() : promo.trim() || null);

  const refreshQuote = async (promoCode) => {
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${API}/register/quote`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ category_id: catId, addon, promo_code: promoCode || null }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Could not price this selection");
      setQuote(data);
      return data;
    } catch (e) {
      setQuote(null);
      setError(e.message);
      return null;
    } finally {
      setBusy(false);
    }
  };

  const sendOtp = async () => {
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${API}/register/otp/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Could not send code");
      setResendsLeft(data.resends_left);
      toast.success("Verification code sent to your email.");
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const verifyOtp = async () => {
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${API}/register/otp/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, code: otpCode }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Verification failed");
      setOtpToken(data.otp_token);
      toast.success("Email verified.");
      setStep(isSpectator ? 4 : 3);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const cleanPhone = (v) => {
    const d = (v || "").replace(/\D/g, "");
    return d.length === 12 && d.startsWith("91") ? d.slice(2) : d;
  };

  const detailsNext = () => {
    const missing = [];
    if (!firstName.trim()) missing.push("first name");
    if (!lastName.trim()) missing.push("last name");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) missing.push("a valid email");
    if (!/^[6-9]\d{9}$/.test(cleanPhone(phone))) missing.push("a valid 10-digit mobile");
    if (!dob) missing.push("date of birth");
    if (!gender) missing.push("gender");
    if (!emgName.trim()) missing.push("emergency contact name");
    if (!/^[6-9]\d{9}$/.test(cleanPhone(emgPhone))) missing.push("a valid emergency contact phone");
    if (isDoubles) {
      if (!teamName.trim()) missing.push("team name");
      if (!pFirst.trim() || !pLast.trim()) missing.push("partner name");
      if (!pDob) missing.push("partner date of birth");
      if (!pGender) missing.push("partner gender");
      if (!/^[6-9]\d{9}$/.test(cleanPhone(pPhone))) missing.push("a valid partner mobile");
      if (catId === "MIXED_DOUBLES" && gender && pGender && gender === pGender)
        missing.push("one male and one female athlete for Mixed Doubles");
    }
    if (!okTerms || !okPrivacy || !okWaiver) missing.push("all three declarations accepted");
    if (missing.length) {
      const msg = `Before continuing, add: ${missing.join(", ")}.`;
      setError(msg);
      toast.error(msg);
      return;
    }
    setError("");
    setStep(2);
  };

  const payNow = async () => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${API}/register/pay`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          category_id: catId,
          addon,
          promo_code: quote?.promo || null,
          otp_token: otpToken,
          first_name: firstName,
          last_name: lastName,
          email,
          phone,
          dob,
          gender,
          emergency_name: emgName,
          emergency_phone: emgPhone,
          team_name: isDoubles ? teamName : null,
          partner: isDoubles
            ? { first_name: pFirst, last_name: pLast, dob: pDob, gender: pGender, phone: pPhone }
            : null,
          marketing_opt_in: marketing,
          consent_version: event?.terms_version || "v1",
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Could not start payment");
      if (data.free) {
        toast.success("Registration confirmed — your QR pass is ready.");
        window.location.href = `/ticket?txnid=${data.txnid}`;
        return;
      }
      submitPayUForm(data.payment_url, data.params);
    } catch (e) {
      setError(e.message);
      toast.error(e.message);
      setBusy(false);
    }
  };

  const applyPromo = async () => {
    const q = await refreshQuote(promo.trim());
    if (q && q.promo) toast.success(`Promo ${q.promo} applied.`);
  };

  const label = "font-mono text-[10px] tracking-[0.25em] text-[#6e6e6a] block mb-2";
  const field = (id, props) => (
    <input id={id} className="field" data-testid={id} {...props} />
  );

  return (
    <main className="min-h-screen bg-[#161614] text-[#f0eee4] font-body antialiased" data-testid="register-page">
      <div className="max-w-6xl mx-auto px-6 py-10 lg:py-16">
        <div className="flex items-center justify-between">
          <Link to="/" data-testid="register-back" className="font-mono text-[11px] tracking-[0.25em] text-[#a3a39f] hover:text-[#f0eee4] transition-colors inline-flex items-center gap-2">
            <ArrowLeft size={14} /> BACK
          </Link>
          <img src="/logo-long.png" alt="TORQ Circuit" className="h-6 w-auto" />
        </div>

        <div
          className="sticky top-0 z-30 -mx-6 mt-6 px-6 py-3 bg-[#161614]/95 backdrop-blur border-b border-[#2e2e2a] flex items-center justify-between lg:hidden"
          data-testid="register-mobile-bar"
        >
          <span className="font-mono text-[10px] tracking-[0.25em] text-[#a3a39f]">
            STEP {step + 1}/5 — {STEPS[step]}
          </span>
          <span className="font-mono text-[11px] tracking-[0.15em]" data-testid="register-mobile-total">
            {quote ? (quote.total_paise ? inr(quote.total_paise) : "FREE") : category ? (category.price_paise ? inr(category.price_paise) : "FREE") : ""}
          </span>
        </div>

        <div className="mt-10 grid lg:grid-cols-[1fr_1.6fr] gap-10 lg:gap-16">
          <div>
            <p className="font-mono text-[10px] tracking-[0.35em] text-[#6e6e6a]">REGISTER NOW</p>
            <h1 className="mt-3 font-display font-extrabold uppercase text-4xl lg:text-5xl leading-[0.9]" data-testid="register-title">
              {event?.title || "TORQ Circuit"}
            </h1>
            <p className="mt-4 font-mono text-[11px] tracking-[0.2em] text-[#a3a39f]" data-testid="register-event-meta">
              {[event?.city, event?.date_text, event?.venue].filter(Boolean).join(" — ")}
            </p>
            {event?.description && (
              <p className="mt-4 text-sm text-[#a3a39f] leading-relaxed">{event.description}</p>
            )}

            <div className="mt-8 border-t border-[#2e2e2a]" data-testid="register-steps">
              {STEPS.map((s, i) => (
                <div key={s} className={`flex items-center gap-4 py-3 border-b border-[#2e2e2a] font-mono text-[11px] tracking-[0.2em] ${i === step ? "text-[#ff5a1f]" : "text-[#6e6e6a]"}`}>
                  <span>{`0${i + 1}`}</span>
                  <span>{s}</span>
                  {i < step && <span className="ml-auto"><ShieldCheck size={13} /></span>}
                </div>
              ))}
            </div>

            {quote && (
              <div className="mt-8 border border-[#2e2e2a] p-5" data-testid="register-summary">
                <p className="font-mono text-[10px] tracking-[0.25em] text-[#6e6e6a]">SUMMARY</p>
                <p className="mt-3 font-display font-bold uppercase text-xl">{quote.category}</p>
                <p className="mt-1 font-mono text-[11px] tracking-[0.15em] text-[#a3a39f]">{quote.addon}</p>
                <div className="mt-4 border-t border-[#2e2e2a] font-mono text-[11px] tracking-[0.12em]">
                  <div className="flex justify-between py-2"><span className="text-[#6e6e6a]">BASE</span><span>{inr(quote.base_paise)}</span></div>
                  {quote.addon_paise > 0 && (
                    <div className="flex justify-between py-2 border-t border-[#2e2e2a]"><span className="text-[#6e6e6a]">ADD-ON</span><span>{inr(quote.addon_paise)}</span></div>
                  )}
                  {quote.discount_paise > 0 && (
                    <div className="flex justify-between py-2 border-t border-[#2e2e2a]" data-testid="register-discount">
                      <span className="text-[#6e6e6a]">PROMO {quote.promo}</span><span>-{inr(quote.discount_paise)}</span>
                    </div>
                  )}
                  {quote.service_fee_paise > 0 && (
                    <div className="flex justify-between py-2 border-t border-[#2e2e2a]"><span className="text-[#6e6e6a]">CONVENIENCE FEE</span><span>{inr(quote.service_fee_paise)}</span></div>
                  )}
                  {quote.gst_paise > 0 && (
                    <div className="flex justify-between py-2 border-t border-[#2e2e2a]"><span className="text-[#6e6e6a]">GST</span><span>{inr(quote.gst_paise)}</span></div>
                  )}
                  <div className="flex justify-between py-3 border-t border-[#454540] text-sm">
                    <span>TOTAL</span><span className="font-bold" data-testid="register-total">{inr(quote.total_paise)}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div>
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              >
                {step === 0 && (
                  <div data-testid="register-step-category">
                    <p className="font-mono text-[10px] tracking-[0.3em] text-[#6e6e6a] mb-6">CHOOSE YOUR CATEGORY</p>
                    <div className="grid sm:grid-cols-2 gap-px bg-[#2e2e2a] border border-[#2e2e2a]">
                      {(event?.categories || []).filter((c) => c.available).sort((a, b) => (a.id === "SPECTATOR" ? 1 : 0) - (b.id === "SPECTATOR" ? 1 : 0)).map((c) => (
                        <button
                          key={c.id}
                          onClick={() => { setCatId(c.id); setQuote(null); }}
                          data-testid={`register-cat-${c.id.toLowerCase()}`}
                          className={`text-left p-6 transition-colors ${catId === c.id ? "bg-[#ff5a1f] text-[#0c0c0a]" : "bg-[#161614] hover:bg-[#1e1e1c]"}`}
                        >
                          <p className="font-mono text-[10px] tracking-[0.25em] opacity-60">{c.id.includes("DOUBLES") ? "TEAM OF 2" : c.id === "SPECTATOR" ? "WATCH & CHEER" : "SINGLES"}</p>
                          <p className="mt-3 font-display font-extrabold uppercase text-2xl leading-none">{c.label}</p>
                          <p className="mt-2 font-display font-bold text-lg">{c.price_paise ? inr(c.price_paise) : "FREE"}</p>
                          {c.price_paise > 0 && <p className="mt-1 font-mono text-[9px] tracking-[0.2em] opacity-50">INCL. ALL TAXES</p>}
                        </button>
                      ))}
                    </div>
                    <button
                      onClick={() => setStep(1)}
                      disabled={!catId}
                      data-testid="register-next-category"
                      className="btn-solid mt-6 w-full bg-[#ff5a1f] text-[#0c0c0a] font-mono text-xs tracking-[0.25em] py-4 hover:bg-white disabled:opacity-40"
                    >
                      CONTINUE
                    </button>
                  </div>
                )}

                {step === 1 && (
                  <div data-testid="register-step-details">
                    <p className="font-mono text-[10px] tracking-[0.3em] text-[#6e6e6a] mb-6">
                      {isDoubles ? "ATHLETE 1 — YOUR DETAILS" : "YOUR DETAILS"}
                    </p>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div><label htmlFor="reg-first" className={label}>FIRST NAME</label>{field("reg-first", { value: firstName, onChange: (e) => setFirstName(e.target.value), required: true })}</div>
                      <div><label htmlFor="reg-last" className={label}>LAST NAME</label>{field("reg-last", { value: lastName, onChange: (e) => setLastName(e.target.value), required: true })}</div>
                      <div><label htmlFor="reg-email" className={label}>EMAIL</label>{field("reg-email", { type: "email", value: email, onChange: (e) => setEmail(e.target.value), required: true })}</div>
                      <div><label htmlFor="reg-phone" className={label}>MOBILE</label>{field("reg-phone", { type: "tel", pattern: "[6-9][0-9]{9}", title: "10-digit Indian mobile", value: phone, onChange: (e) => setPhone(e.target.value), required: true })}</div>
                      <div><label className={label}>DATE OF BIRTH (DD/MM/YYYY)</label><DobPicker testId="reg-dob" value={dob} onChange={setDob} /></div>
                      <div>
                        <label htmlFor="reg-gender" className={label}>GENDER</label>
                        <select id="reg-gender" className="field" data-testid="reg-gender" value={gender} onChange={(e) => setGender(e.target.value)} required>
                          <option value="">SELECT</option>
                          <option value="MALE">MALE</option>
                          <option value="FEMALE">FEMALE</option>
                          <option value="OTHER">OTHER</option>
                        </select>
                      </div>
                      <div><label htmlFor="reg-emg-name" className={label}>EMERGENCY CONTACT NAME</label>{field("reg-emg-name", { value: emgName, onChange: (e) => setEmgName(e.target.value), required: true })}</div>
                      <div><label htmlFor="reg-emg-phone" className={label}>EMERGENCY CONTACT PHONE</label>{field("reg-emg-phone", { type: "tel", value: emgPhone, onChange: (e) => setEmgPhone(e.target.value), required: true })}</div>
                    </div>

                    {isDoubles && (
                      <div className="mt-8" data-testid="register-partner-block">
                        <p className="font-mono text-[10px] tracking-[0.3em] text-[#6e6e6a] mb-4">
                          ATHLETE 2 — PARTNER DETAILS{catId === "MIXED_DOUBLES" ? " (MIXED: ONE MALE + ONE FEMALE)" : ""}
                        </p>
                        <div className="grid sm:grid-cols-2 gap-4">
                          <div className="sm:col-span-2"><label htmlFor="reg-team" className={label}>TEAM NAME</label>{field("reg-team", { value: teamName, onChange: (e) => setTeamName(e.target.value), required: true })}</div>
                          <div><label htmlFor="reg-p-first" className={label}>PARTNER FIRST NAME</label>{field("reg-p-first", { value: pFirst, onChange: (e) => setPFirst(e.target.value), required: true })}</div>
                          <div><label htmlFor="reg-p-last" className={label}>PARTNER LAST NAME</label>{field("reg-p-last", { value: pLast, onChange: (e) => setPLast(e.target.value), required: true })}</div>
                          <div><label className={label}>PARTNER DATE OF BIRTH (DD/MM/YYYY)</label><DobPicker testId="reg-p-dob" value={pDob} onChange={setPDob} /></div>
                          <div>
                            <label htmlFor="reg-p-gender" className={label}>PARTNER GENDER</label>
                            <select id="reg-p-gender" className="field" data-testid="reg-p-gender" value={pGender} onChange={(e) => setPGender(e.target.value)} required>
                              <option value="">SELECT</option>
                              <option value="MALE">MALE</option>
                              <option value="FEMALE">FEMALE</option>
                              <option value="OTHER">OTHER</option>
                            </select>
                          </div>
                          <div><label htmlFor="reg-p-phone" className={label}>PARTNER MOBILE</label>{field("reg-p-phone", { type: "tel", pattern: "[6-9][0-9]{9}", value: pPhone, onChange: (e) => setPPhone(e.target.value), required: true })}</div>
                        </div>
                      </div>
                    )}

                    <div className="mt-8 space-y-3" data-testid="register-declarations">
                      {[
                        ["reg-ok-terms", okTerms, setOkTerms, (
                          <>I have read and accept the <a href="/terms" target="_blank" rel="noreferrer" className="underline underline-offset-4 decoration-[#454540] hover:text-[#f0eee4] transition-colors">Event Terms &amp; Conditions</a> ({event?.terms_version || "v1"}) and the <a href="/refund-policy" target="_blank" rel="noreferrer" className="underline underline-offset-4 decoration-[#454540] hover:text-[#f0eee4] transition-colors">Ticket Protection &amp; Refund Policy</a>.</>
                        )],
                        ["reg-ok-privacy", okPrivacy, setOkPrivacy, (
                          <>I acknowledge the <a href="/privacy-policy" target="_blank" rel="noreferrer" className="underline underline-offset-4 decoration-[#454540] hover:text-[#f0eee4] transition-colors">Privacy Policy</a> and consent to my details being used to run this event.</>
                        )],
                        ["reg-ok-waiver", okWaiver, setOkWaiver, (
                          <>I accept the liability waiver in the <a href="/terms" target="_blank" rel="noreferrer" className="underline underline-offset-4 decoration-[#454540] hover:text-[#f0eee4] transition-colors">Terms</a> and race at my own risk.</>
                        )],
                      ].map(([id, val, setter, text]) => (
                        <label key={id} className="flex items-start gap-3 cursor-pointer font-mono text-[11px] tracking-[0.05em] text-[#a3a39f] leading-relaxed">
                          <input id={id} data-testid={id} type="checkbox" checked={val} onChange={(e) => setter(e.target.checked)} className="mt-0.5 accent-[#f0eee4]" />
                          {text}
                        </label>
                      ))}
                      <label className="flex items-start gap-3 cursor-pointer font-mono text-[11px] tracking-[0.05em] text-[#6e6e6a] leading-relaxed">
                        <input id="reg-marketing" data-testid="reg-marketing" type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)} className="mt-0.5 accent-[#f0eee4]" />
                        Optional: send me training tips and future event news.
                      </label>
                    </div>

                    <div className="mt-6 flex gap-3">
                      <button onClick={() => setStep(0)} data-testid="register-back-details" className="btn-solid border border-[#454540] font-mono text-xs tracking-[0.25em] px-6 py-4 hover:border-[#f0eee4]">BACK</button>
                      <button
                        onClick={detailsNext}
                        data-testid="register-next-details"
                        className="btn-solid flex-1 bg-[#ff5a1f] text-[#0c0c0a] font-mono text-xs tracking-[0.25em] py-4 hover:bg-white"
                      >
                        CONTINUE — VERIFY EMAIL
                      </button>
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div data-testid="register-step-otp">
                    <p className="font-mono text-[10px] tracking-[0.3em] text-[#6e6e6a] mb-6">VERIFY YOUR EMAIL</p>
                    <p className="text-sm text-[#a3a39f] leading-relaxed">
                      We sent a 6-digit code to <span className="text-[#f0eee4]">{email}</span>. It expires in 10 minutes.
                    </p>
                    <div className="mt-6">
                      <label htmlFor="reg-otp" className={label}>VERIFICATION CODE</label>
                      <input
                        id="reg-otp"
                        className="field text-center tracking-[0.5em] text-xl min-h-[56px]"
                        data-testid="reg-otp"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        autoComplete="one-time-code"
                        maxLength={6}
                        value={otpCode}
                        onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))}
                        placeholder="——————"
                      />
                    </div>
                    <button
                      onClick={verifyOtp}
                      disabled={busy || otpCode.length !== 6}
                      data-testid="register-otp-verify"
                      className="btn-solid mt-6 w-full bg-[#ff5a1f] text-[#0c0c0a] font-mono text-xs tracking-[0.25em] py-4 hover:bg-white disabled:opacity-40"
                    >
                      {busy ? <Loader2 size={14} className="animate-spin inline" /> : "VERIFY"}
                    </button>
                    <button
                      onClick={sendOtp}
                      disabled={busy || resendsLeft === 0}
                      data-testid="register-otp-resend"
                      className="mt-3 w-full font-mono text-[10px] tracking-[0.2em] text-[#a3a39f] hover:text-[#f0eee4] py-3 border border-[#2e2e2a] hover:border-[#454540] transition-colors disabled:opacity-40"
                    >
                      {resendsLeft === null ? "RESEND CODE" : `RESEND CODE (${resendsLeft} LEFT)`}
                    </button>
                  </div>
                )}

                {step === 3 && (
                  <div data-testid="register-step-addon">
                    <p className="font-mono text-[10px] tracking-[0.3em] text-[#6e6e6a] mb-6">TICKET PROTECTION</p>
                    <div className="space-y-px border border-[#2e2e2a]">
                      {Object.entries(addons).map(([key, a]) => (
                        <button
                          key={key}
                          onClick={() => setAddon(key)}
                          data-testid={`register-addon-${key.replace("_", "-")}`}
                          className={`w-full text-left p-6 transition-colors ${addon === key ? "bg-[#ff5a1f] text-[#0c0c0a]" : "bg-[#161614] hover:bg-[#1e1e1c]"}`}
                        >
                          <div className="flex items-start gap-4">
                            <span
                              data-testid={`register-addon-tick-${key.replace("_", "-")}`}
                              className={`mt-1 w-5 h-5 shrink-0 border flex items-center justify-center transition-colors ${addon === key ? "border-[#161614] bg-[#161614] text-[#f0eee4]" : "border-[#454540] text-transparent"}`}
                            >
                              <Check size={13} strokeWidth={3} />
                            </span>
                            <div className="flex-1">
                              <div className="flex items-baseline justify-between gap-4">
                                <span className="font-display font-extrabold uppercase text-2xl">{a.label}</span>
                                <span className="font-display font-bold text-xl">{a.price_paise ? `+${inr(a.price_paise)}` : "INCLUDED"}</span>
                              </div>
                              <ul className={`mt-3 space-y-1 font-mono text-[11px] tracking-[0.05em] ${addon === key ? "text-[#161614]/70" : "text-[#a3a39f]"}`}>
                                {(a.bullets || []).map((b) => <li key={b}>— {b}</li>)}
                              </ul>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                    <p className="mt-4 font-mono text-[10px] tracking-[0.1em] text-[#6e6e6a] leading-relaxed" data-testid="register-addon-disclosure">
                      Lite Flex and Full Flex are added to your entry fee at checkout and are governed by the <a href="/refund-policy" target="_blank" rel="noreferrer" className="underline underline-offset-4 decoration-[#454540] hover:text-[#f0eee4] transition-colors">Ticket Protection &amp; Refund Policy</a>. Registrations are non-refundable for voluntary cancellation under every plan.
                    </p>
                    <div className="mt-6 flex gap-3">
                      <button onClick={() => setStep(1)} data-testid="register-back-addon" className="btn-solid border border-[#454540] font-mono text-xs tracking-[0.25em] px-6 py-4 hover:border-[#f0eee4]">BACK</button>
                      <button
                        onClick={() => setStep(4)}
                        data-testid="register-next-addon"
                        className="btn-solid flex-1 bg-[#ff5a1f] text-[#0c0c0a] font-mono text-xs tracking-[0.25em] py-4 hover:bg-white"
                      >
                        REVIEW & PAY
                      </button>
                    </div>
                  </div>
                )}

                {step === 4 && (
                  <div data-testid="register-step-review">
                    <p className="font-mono text-[10px] tracking-[0.3em] text-[#6e6e6a] mb-6">REVIEW & PAY</p>
                    <div className="border border-[#2e2e2a] p-6 font-mono text-[11px] tracking-[0.1em] space-y-2" data-testid="register-review-summary">
                      <div className="flex justify-between"><span className="text-[#6e6e6a]">ATHLETE</span><span className="text-right">{firstName} {lastName}{isDoubles && pFirst ? ` + ${pFirst} ${pLast}` : ""}</span></div>
                      <div className="flex justify-between"><span className="text-[#6e6e6a]">EMAIL</span><span className="text-right break-all">{email}</span></div>
                      <div className="flex justify-between"><span className="text-[#6e6e6a]">CATEGORY</span><span>{quote?.category}</span></div>
                      {!isSpectator && <div className="flex justify-between"><span className="text-[#6e6e6a]">ADD-ON</span><span>{quote?.addon}</span></div>}
                      {isDoubles && <div className="flex justify-between"><span className="text-[#6e6e6a]">TEAM</span><span>{teamName}</span></div>}
                      {quote && (
                        <>
                          <div className="flex justify-between border-t border-[#2e2e2a] pt-2"><span className="text-[#6e6e6a]">BASE FEE</span><span>{inr(quote.base_paise)}</span></div>
                          {quote.addon_paise > 0 && <div className="flex justify-between"><span className="text-[#6e6e6a]">TICKET PROTECTION</span><span>{inr(quote.addon_paise)}</span></div>}
                          {quote.discount_paise > 0 && <div className="flex justify-between"><span className="text-[#6e6e6a]">PROMO DISCOUNT</span><span>−{inr(quote.discount_paise)}</span></div>}
                          {quote.service_fee_paise > 0 && <div className="flex justify-between"><span className="text-[#6e6e6a]">CONVENIENCE FEE ({quote.service_fee_percent}%)</span><span>{inr(quote.service_fee_paise)}</span></div>}
                          {quote.gst_paise > 0 && <div className="flex justify-between"><span className="text-[#6e6e6a]">GST ({quote.gst_percent}%)</span><span>{inr(quote.gst_paise)}</span></div>}
                          <div className="flex justify-between border-t border-[#2e2e2a] pt-2 font-bold text-[#f0eee4]"><span>TOTAL PAYABLE</span><span>{inr(quote.total_paise)}</span></div>
                        </>
                      )}
                    </div>

                    {!isSpectator && (
                    <div className="mt-6">
                      <label htmlFor="reg-promo" className={label}>PROMO CODE</label>
                      <div className="flex gap-3">
                        <input id="reg-promo" className="field flex-1" data-testid="reg-promo" placeholder="CODE" value={promo} onChange={(e) => setPromo(e.target.value.toUpperCase())} />
                        <button onClick={applyPromo} disabled={busy || !promo.trim()} data-testid="reg-promo-apply" className="btn-solid border border-[#454540] font-mono text-xs tracking-[0.2em] px-6 hover:border-[#f0eee4] disabled:opacity-40">
                          APPLY
                        </button>
                      </div>
                    </div>
                    )}

                    <button
                      onClick={payNow}
                      disabled={busy || !quote}
                      data-testid="register-pay-now"
                      className="btn-solid mt-8 w-full bg-[#ff5a1f] text-[#0c0c0a] font-mono text-xs tracking-[0.25em] py-4 hover:bg-white disabled:opacity-40"
                    >
                      {busy ? (
                        <span className="inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> {isSpectator ? "CONFIRMING…" : "REDIRECTING TO PAYU…"}</span>
                      ) : (
                        isSpectator ? "CONFIRM FREE REGISTRATION" : `PAY ${quote ? inr(quote.total_paise) : ""} — CONFIRM BOOKING`
                      )}
                    </button>
                    <p className="mt-3 font-mono text-[10px] tracking-[0.1em] text-[#6e6e6a] text-center">
                      {isSpectator
                        ? "Free entry — your QR pass is issued instantly after confirmation."
                        : "Your seat is held for 12 minutes while you pay. Secure checkout by PayU."}
                    </p>
                  </div>
                )}

                {error && (
                  <p className="mt-6 font-mono text-[11px] tracking-[0.15em] text-[#f0eee4] border border-[#454540] px-4 py-3" data-testid="register-error">
                    {error}
                  </p>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </main>
  );
}
